import { i18n } from "../i18n";
import { Route } from "vue-router";
import moment from "moment-timezone";
import axios from "axios";

const Localize = {
  get defaultLocale(): string {
    return process.env.VUE_APP_I18N_LOCALE || "en";
  },

  get supportedLocales(): string[] {
    return process.env.VUE_APP_I18N_SUPPORTED_LOCALES?.split(",") || ["en"];
  },

  get currentlocale(): string {
    return i18n.locale;
  },

  set currentlocale(locale: string) {
    i18n.locale = locale;
  },

  getUserSupportedLocale(): string {
    const userPreferredLocale = Localize.getUserLocale();

    if (Localize.isLocaleSupported(userPreferredLocale.locale)) {
      return userPreferredLocale.locale;
    }

    if (Localize.isLocaleSupported(userPreferredLocale.localeNoIso)) {
      return userPreferredLocale.localeNoIso;
    }

    return Localize.defaultLocale;
  },

  getUserLocale(): { locale: string; localeNoIso: string } {
    const locale = window.navigator.language || Localize.defaultLocale;

    return {
      locale: locale,
      localeNoIso: locale.split("-")[0]
    };
  },

  getSupportedTimeZonesList(): string[] {
    const supportedTimezones = moment.tz
      .names()
      .filter(
        item =>
          !item.match(
            /(Etc|EET|CET|MET|WET|GB|GB-Eire|GMT|Universal|HST|MST|PST8PDT|MST7MDT|EST|CST6CDT|EST5EDT|NZ-CHAT|NZ|ROK|ROC|PRC|W-SU|UCT)/
          )
      );

    return supportedTimezones;
  },

  getSupportedLanguagesList(): { key: string; label: string }[] {
    const supportedLanguageOptions = [
      { key: "fi", label: "Suomi" },
      { key: "sv", label: "Svenska" },
      { key: "de", label: "Deutsch" },
      { key: "en", label: "English" },
      { key: "es", label: "Español" },
      { key: "fr", label: "Francés" }
    ];
    return supportedLanguageOptions;
  },

  changeLocale(locale: string): Promise<string> {
    if (!Localize.isLocaleSupported(locale))
      return Promise.reject(new Error("Locale not supported"));

    return Localize.loadLocaleFile(locale).then(msgs => {
      i18n.setLocaleMessage(locale, msgs.default || msgs);
      return Localize.setI18nLocaleInServices(locale);
    });
  },

  isLocaleSupported(locale: string): boolean {
    return Localize.supportedLocales?.includes(locale);
  },

  loadLocaleFile(locale: string) {
    return import(`@/locales/${locale}.json`);
  },

  setI18nLocaleInServices(locale: string): string {
    Localize.currentlocale = locale;
    axios.defaults.headers.common["Accept-language"] = locale;
    document.querySelector("html")?.setAttribute("lang", locale);
    return locale;
  },

  routeMiddleware(to: Route, from: Route, next: Function) {
    const locale = to.params.locale;
    !Localize.isLocaleSupported(locale)
      ? next(Localize.getUserSupportedLocale())
      : Localize.changeLocale(locale).then(() => next());
  },

  i18nRoute(to: Route) {
    return {
      ...to,
      params: { locale: this.currentlocale, ...to.params }
    };
  },

  getRouteAliases(route: string, perRoute = false): string | string[] {
    interface RouteAliases {
      locale: string;
      routes: {
        [key: string]: string;
      };
    }
    const routeAliases: RouteAliases[] = [
      {
        locale: "en",
        routes: {
          login: "login",
          logout: "logout",
          register: "register",
          resetpassword: "reset-password",
          unsubscribe: "unsubscribe",
          customerdashboard: "customer-dashboard",
          paymentmethodredirect: "payment-method-redirect-en"
        }
      },
      {
        locale: "fi",
        routes: {
          login: "kirjaudu-sisaan",
          logout: "kirjaudu-ulos",
          register: "rekisteroidy",
          resetpassword: "nollaa-salasana",
          unsubscribe: "lopeta-tilaus",
          customerdashboard: "yleisnakyma",
          paymentmethodredirect: "payment-method-redirect-fi"
        }
      },
      {
        locale: "de",
        routes: {
          login: "einloggen",
          logout: "ausloggen",
          register: "registrieren",
          resetpassword: "passwort-zurucksetzen",
          unsubscribe: "abbestellen",
          customerdashboard: "customer-dashboard",
          paymentmethodredirect: "payment-method-redirect-de"
        }
      },
      {
        locale: "fr",
        routes: {
          login: "connexion",
          logout: "deconnecte",
          register: "s-inscrire",
          resetpassword: "reinitialiser-le-mot-de-passe",
          unsubscribe: "desabonne",
          customerdashboard: "customer-dashboard",
          paymentmethodredirect: "payment-method-redirect-fr"
        }
      }
    ];
    if (perRoute) {
      return routeAliases.filter(
        alias => alias.locale === this.currentlocale
      )[0].routes[route];
    } else {
      return routeAliases
        .filter(alias => alias.locale !== "en")
        .reduce((acc, crr, idx) => {
          return idx === 0
            ? [crr.routes[route as string]]
            : [...acc, crr.routes[route as string]];
        }, [] as string[]);
    }
  }
};

export { Localize };
